/**
 * @file: The marks on one bar of the grid.
 *
 * A bar gets classes for the selection, for the never-on-screen mark and for
 * its height. A bar also gets the stripe colour of its hatch, and diagonals on
 * the stretches that a higher band covers. FullCalendar calls these functions
 * from its render hooks. The functions keep no state.
 */

import { hatchOn } from 'common/playlist-colour'
import { fractionOfDay } from './grid-geometry'
import { sameIntervalId } from './calendar-intervals'
import { barMinutes, fillsAtMostOneRow } from './bar-content'
import { asMs, coveredFractionsOf } from '../../../shared/on-screen-segments'

/**
 * Is the interval of this bar never on screen in the visible range?
 *
 * The mark on the bar and the words in the tooltip both use this function.
 *
 * @param {Object} week - The snapshot from CalendarManager.week
 * @param {Object} event - A FullCalendar event
 */
export function isNeverOnScreen (week, event) {
  return Boolean(event && event.extendedProps && week.neverOnScreenIds.has(event.extendedProps.intervalId))
}

/**
 * Where to place a background layer that covers one stretch of a bar.
 *
 * A background position in percent is a share of the space that the layer
 * does not fill. It is not a share of the box. For example, a layer with a
 * quarter of the height that starts a quarter of the way down has the
 * position of one third.
 *
 * A layer that fills the box has no space left, so it has no position. The
 * caller does not use this function for such a layer. A bar that is covered
 * for its whole run keeps its warning.
 *
 * @param {number} from - Where the stretch starts, as a share of the bar
 * @param {number} size - How much of the bar the stretch covers
 * @returns {number} The percentage for background-position
 */
export const layerPositionPercent = (from, size) => (from / (1 - size)) * 100

/**
 * The covered stretches of one bar, as shares of that bar.
 *
 * FullCalendar draws a run that crosses midnight as one bar for each day. Each
 * bar is the part of the run inside its day column. A share of the whole run
 * therefore puts the stretch in the wrong place on every such bar.
 *
 * The day shares come from the module that places the strip. On the two days
 * each year when the clock changes, a stretch and the strip then match.
 *
 * @param {Array} stretches - [{ from, to }] as fractions of the whole run
 * @param {Object} run - { start, end } of the interval
 * @param {string|null} date - 'YYYY-MM-DD' of the bar's day column. With no
 *   date, the bar is taken to show the whole run.
 * @returns {Array} [{ from, to }] as fractions of the bar
 */
export function stretchesOnBar (stretches, run, date) {
  if (!date) {
    return stretches
  }

  const start = asMs(run.start)
  const length = asMs(run.end) - start
  const dayShareAt = runShare => fractionOfDay(date, start + runShare * length)
  const barFrom = Math.max(0, dayShareAt(0))
  const barLength = Math.min(1, dayShareAt(1)) - barFrom

  if (!(barLength > 0)) {
    return []
  }

  return stretches
    .map(stretch => ({
      from: Math.max(0, dayShareAt(stretch.from) - barFrom) / barLength,
      to: Math.min(barLength, dayShareAt(stretch.to) - barFrom) / barLength
    }))
    .filter(stretch => stretch.to > stretch.from)
}

/** The date of the day column that holds a bar, or null outside a column. */
function dayColumnOf (el) {
  const column = el.closest ? el.closest('.fc-timegrid-col[data-date]') : null

  return column ? column.getAttribute('data-date') : null
}

/**
 * The diagonal lines that say a stretch is covered.
 *
 * The pattern is in the LESS file, because the never-on-screen bar and the
 * legend draw the same lines. One mark then means one thing on every surface.
 */
const COVERED_HATCH = 'var(--schedule-hatch)'

/** The property that gives the pattern the stripe colour of one bar. */
const HATCH_STRIPE = '--schedule-hatch-stripe'

/**
 * Give the hatch of one bar the stripe colour for its fill.
 *
 * The covered stretches and the never-on-screen mark both draw the hatch.
 * The never-on-screen bar gets no covered stretch, so this is a step of its
 * own.
 *
 * @param {Object} arg - The eventDidMount argument of FullCalendar
 */
export function inkHatch ({ event, el, backgroundColor }) {
  if (!el || !event || !event.extendedProps) {
    return
  }

  el.style.setProperty(HATCH_STRIPE, hatchOn(backgroundColor))
}

/**
 * Draw the stretches of a bar where a higher band is on screen instead.
 *
 * A base interval can be on screen, then covered by a takeover, then on
 * screen again. The whole-bar warning cannot show that. Without this mark,
 * the operator compared the bar with the strip below the grid to find when
 * the interval plays.
 *
 * Each covered stretch gets one background layer. The size and the position
 * of the layer are shares of the bar. The height of the bar is the length of
 * the interval at every zoom level. A share is therefore correct at every
 * zoom level, and the code measures nothing.
 *
 * background-origin is the border box, so a stretch aligns with the times of
 * the bar. The first version used child elements. A percentage on an
 * absolutely placed child uses the padding box, which has no borders. Each
 * stretch was then two pixels too low and four pixels too short.
 *
 * A background layer paints under the text without a stacking order. It also
 * gets no pointer events.
 *
 * A bar that is covered for its whole run keeps its warning and gets no
 * diagonals. The two marks then never show the same fact twice.
 *
 * The bar that follows a drag or a resize shows new times. The covered
 * stretches belong to the stored times, so that bar gets no diagonals.
 *
 * @param {Array} intervals - The intervals of the snapshot from CalendarManager.week
 * @param {Object} arg - The eventDidMount argument of FullCalendar
 */
export function markCoveredStretches (intervals, { event, el, isMirror }) {
  const props = event && event.extendedProps

  if (!el || !props) {
    return
  }

  if (isMirror) {
    return
  }

  const mine = intervals.find(interval => sameIntervalId(interval.id, props.intervalId))

  if (!mine) {
    return
  }

  const stretches = coveredFractionsOf(intervals, mine)
  const wholeRun = stretches.length === 1 && stretches[0].from === 0 && stretches[0].to === 1

  if (wholeRun) {
    return
  }

  const onBar = stretchesOnBar(stretches, mine, dayColumnOf(el))

  if (onBar.length === 0) {
    return
  }

  const sizes = onBar.map(stretch => (stretch.to - stretch.from))
  // A layer that fills the bar has no space left over to place it in.
  const position = (stretch, size) => size >= 1 ? 0 : layerPositionPercent(stretch.from, size)

  el.style.backgroundImage = onBar.map(() => COVERED_HATCH).join(', ')
  el.style.backgroundOrigin = 'border-box'
  el.style.backgroundClip = 'border-box'
  el.style.backgroundRepeat = onBar.map(() => 'no-repeat').join(', ')
  el.style.backgroundSize = sizes.map(size => `100% ${(size * 100).toFixed(3)}%`).join(', ')
  el.style.backgroundPosition = onBar
    .map((stretch, index) => `0 ${position(stretch, sizes[index]).toFixed(3)}%`)
    .join(', ')
}

/**
 * Give the classes of one bar.
 *
 * There are four classes, and each one shows a different fact. One class
 * marks the bar that the panel shows. One marks the bar of the Preflight row
 * that the operator points at. One marks a bar that never reaches the screen
 * in the visible range. One marks a bar with the height of one slot row or
 * less. Such a bar has no space for two lines with the full decoration.
 *
 * @param {Object} arg - The eventClassNames argument of FullCalendar
 * @returns {Array<string>}
 */
export function barClasses (manager, arg) {
  if (!arg.event || !arg.event.extendedProps) return []

  const classes = []
  const inspecting = manager.controller.stateManager && manager.controller.stateManager.inspecting

  if (inspecting && sameIntervalId(inspecting.intervalId, arg.event.extendedProps.intervalId)) {
    classes.push('fc-event-inspecting')
  }

  if (sameIntervalId(manager.highlightedIntervalId, arg.event.extendedProps.intervalId)) {
    classes.push('fc-event-preflight-target')
  }

  if (isNeverOnScreen(manager.week(), arg.event)) {
    classes.push('fc-event-never-on-screen')
  }

  // A bar with the height of one slot row or less gets less decoration, so its
  // two text lines fit. The test compares with the row and not with a number
  // of minutes. A minute has a different height in pixels at each zoom
  // level. The minutes are the minutes of this bar, as in barContent. An
  // interval over midnight therefore gets the class for each day. The rule
  // for this class is in the LESS file.
  if (arg.event.start && arg.event.end &&
        fillsAtMostOneRow(barMinutes(arg), manager.slotZoom.currentSlotMinutes())) {
    classes.push('fc-event-one-row')
  }

  return classes
}
